import { getDealRevenue, getDealInstallmentCents } from '../../../../shared/utils/dealRevenue';
import { entitySelectionPayload } from '../../../../shared/utils/entitySelection';
import { hasSelectedEntity } from '../../../../shared/utils/entitySelection';
import { useEffect, useMemo, useState } from 'react';
import { useNavigate, useSearchParams } from 'react-router-dom';
import { useSession } from '@/app/providers/session-provider';
import { listClientsResult } from '@/modules/clients/services/clients.service';
import { listStaffResult } from '@/modules/staff/services';
import {
  getCompletedDealsStaticConfig,
  getCompletedDealSettlements,
  deleteCompletedDeal as deleteCompletedDealMutation,
  listCompletedDealsResult,
  subscribeToCompletedCreated,
  updateCompletedDealStaff,
  updateCompletedDealStage,
} from '@/modules/deals/services';
import {
  buildDealLocationByType,
} from '@/modules/deals/shared/utils';
import { calculateCompletedStats, filterCompletedByOverview, getCompletedVisibleStages } from '../../utils/completed/completed.helpers';
import { normalizeSearchValue } from '../../utils/proposals/proposals.helpers';

const { dealsCompleted: initialDeals, dealStages } = getCompletedDealsStaticConfig();
const ACCOUNTING_INVOICE_CREATED_EVENT = 'accounting:invoice-created';

function normalizeClientId(clientId) {
  if (clientId === null || clientId === undefined || clientId === '') {
    return '';
  }

  return String(clientId);
}

function getClientDisplayName(client) {
  return client?.company || client?.businessName || client?.name || '';
}

function buildClientsMap(clients) {
  return new Map(
    (clients || [])
      .map((client) => [normalizeClientId(client?.id), client])
      .filter(([clientId]) => clientId)
  );
}

function resolveClientReference({ clientId, legacyClientName, clientsMap }) {
  const normalizedClientId = normalizeClientId(clientId);
  const legacyName = String(legacyClientName || '').trim();

  if (!normalizedClientId) {
    return {
      clientResolvedName: legacyName || 'Cliente non risolto',
      clientResolutionStatus: legacyName ? 'legacy_fallback' : 'missing_client_id',
      clientLegacyName: legacyName,
    };
  }

  const client = clientsMap.get(normalizedClientId);
  if (client) {
    return {
      clientResolvedName:
        getClientDisplayName(client) || legacyName || `Cliente ${normalizedClientId}`,
      clientResolutionStatus: 'resolved',
      clientLegacyName: legacyName,
    };
  }

  return {
    clientResolvedName: legacyName || 'Cliente non risolto',
    clientResolutionStatus: 'unresolved_client_reference',
    clientLegacyName: legacyName,
  };
}

function enrichDealsWithClientResolution(deals, clients) {
  const clientsMap = buildClientsMap(clients);

  return (deals || []).map((deal) => {
    const resolution = resolveClientReference({
      clientId: deal.clientId,
      legacyClientName: deal.client,
      clientsMap,
    });

    return {
      ...deal,
      ...resolution,
      client: resolution.clientResolvedName,
    };
  });
}

export function useCompletedDealsData() {
  const { logout } = useSession();
  const navigate = useNavigate();
  const [searchParams] = useSearchParams();
  const clientFilterParam = normalizeClientId(searchParams.get('clientId'));

  const [rawDeals, setRawDeals] = useState(initialDeals);
  const [staff, setStaff] = useState([]);
  const [clients, setClients] = useState([]);
  const [isLoadingCompletedDeals, setIsLoadingCompletedDeals] = useState(true);
  const [completedDealsModuleDisabled, setCompletedDealsModuleDisabled] = useState(false);
  const [completedDealsUnavailable, setCompletedDealsUnavailable] = useState(false);
  const [completedDealsError, setCompletedDealsError] = useState('');
  const [completedDealsAuthRequired, setCompletedDealsAuthRequired] = useState(false);
  const [completedDealsTenantDatabaseBlocked, setCompletedDealsTenantDatabaseBlocked] = useState(false);
  const [staffUnavailable, setStaffUnavailable] = useState(false);
  const [staffError, setStaffError] = useState('');
  const [clientsUnavailable, setClientsUnavailable] = useState(false);
  const [clientsError, setClientsError] = useState('');
  const [selectedDeal, setSelectedDeal] = useState(null);
  const [isDetailDialogOpen, setIsDetailDialogOpen] = useState(false);
  const [completedDealsReloadToken, setCompletedDealsReloadToken] = useState(0);
  const [settlementsByCompletedDealId, setSettlementsByCompletedDealId] = useState({});
  const [settlementBatchState, setSettlementBatchState] = useState({
    status: 'idle',
    message: '',
  });
  const [filters, setFilters] = useState({
    searchTerm: '',
    statusFilter: 'all',
    assigneeFilter: 'all',
    stageFilter: 'all',
    clientFilter: clientFilterParam || 'all',
  });
  const [activeOverviewFilter, setActiveOverviewFilter] = useState(null);

  useEffect(() => {
    setFilters((prev) => {
      const nextClientFilter = clientFilterParam || prev.clientFilter || 'all';
      if (prev.clientFilter === nextClientFilter) return prev;
      return { ...prev, clientFilter: nextClientFilter };
    });
  }, [clientFilterParam]);

  useEffect(() => {
    let cancelled = false;

    async function loadDeals() {
      const result = await listCompletedDealsResult();

      if (!cancelled) {
        const moduleDisabled = result.error === 'DEALS_MODULE_DISABLED';
        const tenantDatabaseBlocked =
          result.error === 'DEALS_TENANT_DATABASE_NOT_READY' ||
          result.error === 'DEALS_TENANT_DATABASE_METADATA_MISSING' ||
          result.error === 'DEALS_TENANT_DATABASE_UNAVAILABLE';
        if (result.error === 'COMPLETED_DEALS_AUTH_REQUIRED') {
          logout();
        }
        setRawDeals(result.ok ? result.data : []);
        setIsLoadingCompletedDeals(false);
        setCompletedDealsModuleDisabled(moduleDisabled);
        setCompletedDealsUnavailable(
          !result.ok && result.error !== 'COMPLETED_DEALS_AUTH_REQUIRED' && !moduleDisabled && !tenantDatabaseBlocked
        );
        setCompletedDealsAuthRequired(result.error === 'COMPLETED_DEALS_AUTH_REQUIRED');
        setCompletedDealsTenantDatabaseBlocked(tenantDatabaseBlocked);
        setCompletedDealsError(result.ok ? '' : result.message || result.error || '');
      }
    }

    loadDeals();

    return () => {
      cancelled = true;
    };
  }, [completedDealsReloadToken, logout]);

  useEffect(() => {
    function reloadAfterInvoiceCreation() {
      setCompletedDealsReloadToken((value) => value + 1);
    }

    window.addEventListener(ACCOUNTING_INVOICE_CREATED_EVENT, reloadAfterInvoiceCreation);
    window.addEventListener('webtika:deal-payments-updated', reloadAfterInvoiceCreation);
    return () => {
      window.removeEventListener(ACCOUNTING_INVOICE_CREATED_EVENT, reloadAfterInvoiceCreation);
      window.removeEventListener('webtika:deal-payments-updated', reloadAfterInvoiceCreation);
    };
  }, []);

  useEffect(() => {
    let cancelled = false;

    async function loadClientsCatalog() {
      const result = await listClientsResult();

      if (!cancelled) {
        if (
          result.error === 'CLIENTS_AUTH_REQUIRED' ||
          result.error === 'CLIENTS_SESSION_REFRESH_REQUIRED' ||
          result.error === 'CLIENTS_SESSION_EXPIRED'
        ) {
          logout();
        }
        setClients(result.ok ? result.data : []);
        setClientsUnavailable(!result.ok);
        setClientsError(result.ok ? '' : result.message || result.error || '');
      }
    }

    loadClientsCatalog();

    return () => {
      cancelled = true;
    };
  }, []);

  useEffect(() => {
    let cancelled = false;

    async function loadStaffCatalog() {
      const result = await listStaffResult();

      if (!cancelled) {
        if (
          result.error === 'STAFF_AUTH_REQUIRED' ||
          result.error === 'STAFF_SESSION_REFRESH_REQUIRED' ||
          result.error === 'STAFF_SESSION_EXPIRED'
        ) {
          logout();
        }
        setStaff(result.ok ? result.data : []);
        setStaffUnavailable(!result.ok);
        setStaffError(result.ok ? '' : result.message || result.error || '');
      }
    }

    loadStaffCatalog();

    return () => {
      cancelled = true;
    };
  }, []);

  useEffect(() => {
    return subscribeToCompletedCreated(({ completedDeal }) => {
      setRawDeals((prev) => {
        const existing = prev.some((item) => item.id === completedDeal.id);

        if (existing) {
          return prev.map((item) => (item.id === completedDeal.id ? completedDeal : item));
        }

        return [completedDeal, ...prev].sort((left, right) =>
          String(right.completedDate || '').localeCompare(String(left.completedDate || ''))
        );
      });
    });
  }, []);

  const deals = useMemo(
    () => enrichDealsWithClientResolution(rawDeals, clients),
    [clients, rawDeals]
  );
  const effectiveClientFilter = clientFilterParam || filters.clientFilter || 'all';
  const visibleDeals = useMemo(() => {
    if (effectiveClientFilter === 'all') return deals;
    return deals.filter((deal) => hasSelectedEntity(deal, 'client', effectiveClientFilter));
  }, [deals, effectiveClientFilter]);
  const activeClientFilterLabel = useMemo(() => {
    if (!effectiveClientFilter || effectiveClientFilter === 'all') return '';
    const client = clients.find((item) => normalizeClientId(item.id) === normalizeClientId(effectiveClientFilter));

    return getClientDisplayName(client) || `Cliente ${effectiveClientFilter}`;
  }, [clients, effectiveClientFilter]);

  useEffect(() => {
    const completedDealIds = visibleDeals
      .map((deal) => deal.id)
      .filter((dealId) => dealId !== null && dealId !== undefined);

    if (completedDealIds.length === 0) {
      setSettlementsByCompletedDealId({});
      setSettlementBatchState({
        status: 'idle',
        message: '',
      });
      return;
    }

    let active = true;

    async function loadSettlements() {
      setSettlementBatchState({
        status: 'loading',
        message: '',
      });

      const result = await getCompletedDealSettlements(completedDealIds);
      if (!active) return;

      if (!result.ok) {
        setSettlementsByCompletedDealId(
          Object.fromEntries(
            completedDealIds.map((completedDealId) => [
              completedDealId,
              {
                state: 'error',
                message: result.message,
              },
            ])
          )
        );
        setSettlementBatchState({
          status:
            result.error === 'SETTLEMENT_BATCH_AUTH_REQUIRED' ? 'auth_required' : 'unavailable',
          message: result.message,
        });
        return;
      }

      const resolvedEntries = Object.fromEntries(
        result.data.map((settlement) => [
          settlement.completedDealId,
          {
            state: 'resolved',
            ...settlement,
          },
        ])
      );

      const missingEntries = Object.fromEntries(
        completedDealIds
          .filter((completedDealId) => !resolvedEntries[completedDealId])
          .map((completedDealId) => [
            completedDealId,
            {
              state: 'missing',
              message: 'Nessun pagamento collegato in accounting.',
            },
          ])
      );

      setSettlementsByCompletedDealId({
        ...resolvedEntries,
        ...missingEntries,
      });
      setSettlementBatchState({
        status: 'success',
        message: '',
      });
    }

    loadSettlements();

    return () => {
      active = false;
    };
  }, [visibleDeals]);

  const visibleStages = useMemo(() => getCompletedVisibleStages(dealStages), []);
  const completedDealsWithSettlement = useMemo(() => {
    return visibleDeals.map((deal) => {
      const settlement = settlementsByCompletedDealId[deal.id];

      if (!settlement) {
        const settlementState =
          settlementBatchState.status === 'loading'
            ? {
                state: 'loading',
                message: 'Verifica settlement in corso.',
              }
            : settlementBatchState.status === 'success'
              ? {
                  state: 'missing',
                  message: 'Nessun pagamento collegato in accounting.',
                }
              : {
                  state: 'error',
                  message:
                    settlementBatchState.message || 'Impossibile verificare il settlement reale in questo momento.',
                };
        return {
          ...deal,
          settlementView: settlementState,
        };
      }

      if (settlement.state === 'resolved') {
        return {
          ...deal,
          paymentStatus: settlement.status,
          settlementView: settlement,
        };
      }

      return {
        ...deal,
        settlementView: settlement,
      };
    });
  }, [visibleDeals, settlementBatchState.status, settlementsByCompletedDealId]);

  const filteredCompletedDealsBeforeOverview = useMemo(() => {
    const search = normalizeSearchValue(filters.searchTerm);

    return completedDealsWithSettlement.filter((deal) => {
      const resolvedSettlementStatus =
        deal.settlementView?.state === 'resolved'
          ? deal.settlementView.status
          : deal.paymentStatus;
      const statusKey =
        deal.settlementView?.state !== 'resolved' &&
        !['da_saldare', 'saldato'].includes(deal.paymentStatus)
          ? 'unavailable'
          : resolvedSettlementStatus;
      const matchesSearch =
        !search ||
        normalizeSearchValue(deal.name).includes(search) ||
        normalizeSearchValue(deal.client).includes(search) ||
        normalizeSearchValue(deal.assignee).includes(search) ||
        normalizeSearchValue(deal.value).includes(search);
      const matchesStatus =
        filters.statusFilter === 'all' || statusKey === filters.statusFilter;
      const matchesAssignee =
        filters.assigneeFilter === 'all' || (deal.assignee || '').split('; ').includes(filters.assigneeFilter);
      const matchesStage =
        filters.stageFilter === 'all' || deal.stage === filters.stageFilter;

      return matchesSearch && matchesStatus && matchesAssignee && matchesStage;
    });
  }, [completedDealsWithSettlement, filters]);
  const filteredCompletedDealsWithSettlement = useMemo(
    () => filterCompletedByOverview(filteredCompletedDealsBeforeOverview, activeOverviewFilter),
    [activeOverviewFilter, filteredCompletedDealsBeforeOverview]
  );
  const dealsWithoutSettlement = useMemo(
    () =>
      filteredCompletedDealsWithSettlement.filter(
        (deal) =>
          deal.settlementView?.state !== 'resolved' &&
          !['da_saldare', 'saldato'].includes(deal.paymentStatus)
      ),
    [filteredCompletedDealsWithSettlement]
  );
  const dealsToSettle = useMemo(
    () =>
      filteredCompletedDealsWithSettlement.filter(
        (deal) =>
          (deal.settlementView?.state === 'resolved' && deal.settlementView.status === 'da_saldare') ||
          (deal.settlementView?.state !== 'resolved' && deal.paymentStatus === 'da_saldare')
      ),
    [filteredCompletedDealsWithSettlement]
  );
  const settledDeals = useMemo(
    () =>
      filteredCompletedDealsWithSettlement.filter(
        (deal) =>
          (deal.settlementView?.state === 'resolved' && deal.settlementView.status === 'saldato') ||
          (deal.settlementView?.state !== 'resolved' && deal.paymentStatus === 'saldato')
      ),
    [filteredCompletedDealsWithSettlement]
  );
  const stats = useMemo(
    () => calculateCompletedStats({ deals: completedDealsWithSettlement }),
    [completedDealsWithSettlement]
  );
  const assigneeOptions = useMemo(() => {
    const dealAssignees = deals.flatMap((deal) => (deal.assignee || '').split('; ')).filter(Boolean);
    const staffNames = staff.map((member) => member.name).filter(Boolean);

    return [...new Set([...dealAssignees, ...staffNames])].sort((a, b) =>
      a.localeCompare(b, 'it-IT')
    );
  }, [deals, staff]);
  const hasActiveFilters = useMemo(
    () =>
      filters.searchTerm.trim() !== '' ||
      filters.statusFilter !== 'all' ||
      filters.assigneeFilter !== 'all' ||
      filters.stageFilter !== 'all' ||
      (filters.clientFilter && filters.clientFilter !== 'all') ||
      Boolean(activeOverviewFilter),
    [activeOverviewFilter, filters]
  );

  function setOverviewFilter(filterKey) {
    setActiveOverviewFilter((prev) => {
      const next = prev === filterKey ? null : filterKey;
      setFilters({
        searchTerm: '',
        statusFilter: 'all',
        assigneeFilter: 'all',
        stageFilter: 'all',
        clientFilter: clientFilterParam || 'all',
      });
      return next;
    });
  }

  function openDealDetails(deal) {
    setSelectedDeal(deal);
    setIsDetailDialogOpen(true);
  }

  function closeDealDetails() {
    setIsDetailDialogOpen(false);
    setSelectedDeal(null);
  }

  function goToCreateDeal() {
    navigate(buildDealLocationByType('inCorso'));
  }

  function openCompletedDealCreateInvoice(deal) {
    navigate('/contabilita-aziendale', {
      state: {
        createInvoicePrefill: {
          completedDealId: deal?.id ?? null,
          installmentAmounts: getDealInstallmentCents(deal).map((cents) => cents / 100),
          clientId: deal?.clientId ?? null,
          client: deal?.clientResolutionStatus === 'resolved'
            ? deal?.clientResolvedName || ''
            : deal?.clientLegacyName || '',
          assignment: deal?.name || '',
          totalAmount: getDealRevenue(deal),
          notes: deal?.notes || '',
          initialPaymentStatus: deal?.paymentStatus || '',
          ...entitySelectionPayload(deal),
          paymentExpectedDate: deal?.paymentExpectedDate || deal?.paymentDate || deal?.dataPagamento || deal?.dilazionamento?.scadenze?.[0] || '',
          installmentCount: deal?.installmentCount ?? deal?.dilazionamento?.rate ?? 1,
          installmentAmount: getDealInstallmentCents(deal)[0] / 100,
          installmentDueDates: deal?.installmentDueDates || deal?.dilazionamento?.scadenze || [],
        },
      },
    });
  }

  function openCompletedDealAccounting(deal) {
    const clientId = normalizeClientId(deal?.clientId);
    navigate(clientId ? `/contabilita-aziendale?clientId=${encodeURIComponent(clientId)}` : '/contabilita-aziendale');
  }

  async function onDragEnd(result) {
    const { destination, source, draggableId } = result;

    if (!destination) return;

    if (
      destination.droppableId === source.droppableId &&
      destination.index === source.index
    ) {
      return;
    }

    const dealId = Number(draggableId);
    const newStage = destination.droppableId;
    const mutationResult = await updateCompletedDealStage(dealId, newStage);

    if (!mutationResult.ok) {
      return mutationResult;
    }

    const updatedDeal = mutationResult.data.deal;
    setRawDeals((prevDeals) =>
      prevDeals.map((deal) => (deal.id === dealId ? { ...deal, ...updatedDeal } : deal))
    );

    if (selectedDeal?.id === dealId) {
      setSelectedDeal((prev) => ({ ...prev, ...updatedDeal }));
    }

    return mutationResult;
  }

  async function handleDeleteCompletedDeal(dealId) {
    const result = await deleteCompletedDealMutation(dealId);
    if (!result.ok) return result;

    setRawDeals((prev) => prev.filter((deal) => deal.id !== dealId));
    if (selectedDeal?.id === dealId) {
      closeDealDetails();
    }
    return result;
  }

  async function handleUpdateCompletedDealStaff(dealId, staffId) {
    const ids = (Array.isArray(staffId) ? staffId : [staffId]).map(String);
    const members = ids.map((id) => staff.find((item) => String(item.id) === id)).filter(Boolean);
    const member = members[0];
    if (!member) {
      return {
        ok: false,
        data: null,
        error: 'COMPLETED_STAFF_INVALID',
        message: 'Seleziona un responsabile staff valido.',
      };
    }

    const result = await updateCompletedDealStaff(dealId, {
      staffId: member.id,
      staffIds: members.map((item) => item.id),
      assignee: members.map((item) => item.name).join('; '),
    });
    if (!result.ok) return result;

    const updatedDeal = result.data.deal;
    setRawDeals((prevDeals) =>
      prevDeals.map((deal) => (deal.id === dealId ? { ...deal, ...updatedDeal } : deal))
    );

    if (selectedDeal?.id === dealId) {
      setSelectedDeal((prev) => ({ ...prev, ...updatedDeal }));
    }

    return result;
  }

  function clearFilters() {
    setFilters({
      searchTerm: '',
      statusFilter: 'all',
      assigneeFilter: 'all',
      stageFilter: 'all',
      clientFilter: clientFilterParam || 'all',
    });
    setActiveOverviewFilter(null);
  }

  return {
    visibleStages,
    dealsWithoutSettlement,
    dealsToSettle,
    settledDeals,
    stats,
    settlementBatchState,
    isLoadingCompletedDeals,
    completedDealsModuleDisabled,
    completedDealsUnavailable,
    completedDealsError,
    completedDealsAuthRequired,
    completedDealsTenantDatabaseBlocked,
    selectedDeal,
    isDetailDialogOpen,
    staff,
    staffUnavailable,
    staffError,
    staffCatalogStatus: staffUnavailable ? 'unavailable' : staff.length > 0 ? 'available' : 'empty',
    clients,
    clientsUnavailable,
    clientsError,
    clientsCatalogStatus: clientsUnavailable ? 'unavailable' : clients.length > 0 ? 'available' : 'empty',
    filters,
    assigneeOptions,
    hasActiveFilters,
    activeClientFilterLabel,
    activeOverviewFilter,
    isClientFiltered: effectiveClientFilter !== 'all',
    setSearchTerm: (value) => setFilters((prev) => ({ ...prev, searchTerm: value })),
    setStatusFilter: (value) => setFilters((prev) => ({ ...prev, statusFilter: value })),
    setAssigneeFilter: (value) => setFilters((prev) => ({ ...prev, assigneeFilter: value })),
    setStageFilter: (value) => setFilters((prev) => ({ ...prev, stageFilter: value })),
    setClientFilter: (value) => setFilters((prev) => ({ ...prev, clientFilter: clientFilterParam || value || 'all' })),
    setOverviewFilter,
    clearFilters,
    openDealDetails,
    closeDealDetails,
    openCompletedDealCreateInvoice,
    openCompletedDealAccounting,
    onDragEnd,
    handleDeleteCompletedDeal,
    handleUpdateCompletedDealStaff,
    goToCreateDeal,
  };
}
